import { Box, Divider, styled, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { LwButton } from 'redesign';
import { useGetDepartmentDetail } from 'api/hooks/department/use-get-department-detail';
import { useGetWorkCreateData, useGetWorkTemplate, useGetWorkTemplates } from 'api';
import { useSiteDetection } from 'shared/hooks';
import { useEffect, useMemo } from 'react';
import { useGetShiftDetail } from 'api/hooks/shift/get-shift-detail';
import { WorkForm } from './work-form';
import { WorkFormCompanyInfo, WorkFormContext, WorkFormMode } from './work-form.types';
import { mapShiftDetailToForm, mapWorkTemplateToForm } from './helpers/mappers';
import { useWorkFormTexts } from './hooks/use-work-form-texts';
import { useWorkFormMutations } from './hooks/use-work-form-mutations';
import { getHelperTexts } from '../shift-forms/components/forms/shared/get-helper-texts/get-helper-texts';

export type WorkFormProviderProps = {
  cancel: () => void;
  companies: WorkFormCompanyInfo[];
  formMode: WorkFormMode;
  selectedShiftIds: string[];
  isHqAdmin: boolean;
  onSuccess: (shiftId: string) => void;
};

export const WorkFormProvider = ({
  cancel,
  companies,
  formMode,
  selectedShiftIds,
  isHqAdmin,
  onSuccess,
}: WorkFormProviderProps) => {
  // Form
  const formMethods = useForm<WorkFormContext>({
    defaultValues: {
      location: null,
      companyOption: null,
    },
    shouldUnregister: false,
  });
  const selectedCompany = formMethods.watch('companyOption');
  const selectedCompanyId = selectedCompany?.value;
  const selectedDepartmentId = formMethods.watch('department');
  const selectedWorkTemplateId = formMethods.watch('jobId');
  const shiftId = formMode != 'create' ? selectedShiftIds[0] : undefined;

  // Query
  const { site, isHQ, isOGP } = useSiteDetection();
  const workTemplatesQuery = useGetWorkTemplates(site, selectedCompanyId, !!selectedCompanyId);
  const shiftCreateDataQuery = useGetWorkCreateData(site, selectedCompanyId, !!selectedCompanyId);
  const workTemplateDataQuery = useGetWorkTemplate(
    site,
    selectedWorkTemplateId,
    !!selectedWorkTemplateId
  );
  const departmentDataQuery = useGetDepartmentDetail(
    site,
    selectedDepartmentId,
    !!selectedDepartmentId
  );
  const shiftQuery = useGetShiftDetail(shiftId);

  const helperTexts = useMemo(
    () =>
      getHelperTexts(
        workTemplateDataQuery.data
          ? {
              breakMinutes: workTemplateDataQuery.data.breakMinutes,
              hourlyRateCents: workTemplateDataQuery.data.freelanceJob?.hourlyRateCents,
            }
          : undefined
      ),
    [
      workTemplateDataQuery.data?.breakMinutes,
      workTemplateDataQuery.data?.freelanceJob?.hourlyRateCents,
    ]
  );

  const [company] = companies;
  useEffect(() => {
    const [company] = companies;
    if (isOGP && company) {
      formMethods.setValue('companyOption', { value: company.id, label: company.name });
    }
  }, [company?.id, isOGP]);

  const defaultValues = useMemo(() => {
    if (formMode === 'edit' || formMode === 'duplicate') {
      return mapShiftDetailToForm(shiftQuery.data);
    }
    return mapWorkTemplateToForm(workTemplateDataQuery.data, departmentDataQuery.data);
  }, [
    formMode,
    shiftQuery.data?.id,
    workTemplateDataQuery.data?.id,
    departmentDataQuery?.data?.department.id,
  ]);
  const { title, subtitle } = useWorkFormTexts(formMode);

  const showCompanySelect = isHQ && formMode === 'create';
  const showTemplateSelect = formMode === 'create';
  const isSubmitDisabled =
    (!selectedCompanyId && showCompanySelect) || (!selectedWorkTemplateId && showTemplateSelect);

  const { onSubmit } = useWorkFormMutations({
    shiftId,
    formMode,
    defaultValues,
    cancel,
    onSuccess,
  });

  return (
    <FormContainer>
      <FormProvider {...formMethods}>
        <FullHeightForm
          name="work-form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            formMethods.handleSubmit(onSubmit)(e);
          }}
        >
          <FormBody>
            <Box>
              <Box>
                <Typography variant="h1" mb={4}>
                  {title}
                </Typography>
                <Typography variant="body1" mb={4}>
                  {subtitle}
                </Typography>
              </Box>
              <Divider />
              <WorkForm
                showCompanySelect={showCompanySelect}
                showTemplateSelect={showTemplateSelect}
                isContractFieldDisabled={
                  !isHqAdmin && formMode !== 'edit' && !!shiftQuery.data?.claimants.length
                }
                isEmploymentCheckboxDisabled={formMode === 'edit'}
                allowDateBeforeToday={
                  isHqAdmin || formMode === 'create' || formMode === 'duplicate'
                }
                companies={companies}
                workTemplates={workTemplatesQuery.data}
                shiftCreateData={shiftCreateDataQuery.data}
                defaultValues={defaultValues}
                departmentData={departmentDataQuery.data}
                helperTexts={helperTexts}
              />
            </Box>
          </FormBody>

          <SidebarActions>
            <LwButton onClick={cancel} color="secondary">
              Sluiten
            </LwButton>
            <LwButton color="primary" type="submit" disabled={isSubmitDisabled}>
              {formMode === 'edit' ? 'Opslaan' : 'Maak aan'}
            </LwButton>
          </SidebarActions>
        </FullHeightForm>
      </FormProvider>
    </FormContainer>
  );
};

const FormContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(8),
}));

const FullHeightForm = styled('form')(() => ({
  height: '100%',
}));

const FormBody = styled(Box)(({ theme }) => ({
  width: '650px',
  overflow: 'auto',
  paddingRight: theme.spacing(2),
  flex: 1,
  height: 'calc(100% - 88px)',
  display: 'flex',
  flexDirection: 'column',
}));

const SidebarActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(8),
}));
